/**
 *
 */

import { useEffect } from "react";

const useDetailsSlide = () => {
    useEffect(() => {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const detailsList = Array.from(document.querySelectorAll<HTMLDetailsElement>(".psa-acc"));

        // Keep handler refs to remove them on cleanup
        const handlerMap = new WeakMap<HTMLElement, {
            toggle: (ev: Event) => void;
            transitionEnd: (ev: TransitionEvent) => void;
        }>();

        // Utility: get current numeric height
        const getHeight = (el: HTMLElement) => el.getBoundingClientRect().height;

        // Utility: force reflow so the browser acknowledges the current style before changing it
        const forceReflow = (el: HTMLElement) => void el.offsetHeight;

        // Cancel any in-flight transition by cloning transition prop
        const cancelTransition = (el: HTMLElement) => {
            const t = getComputedStyle(el).transition;
            el.style.transition = "none";
            // force reflow to apply 'none'
            forceReflow(el);
            // restore
            el.style.transition = t;
            forceReflow(el);
        };

        const setTransition = (el: HTMLElement, t = "height .5s ease") => {
            el.style.transition = t;
        };

        const openAnim = (anim: HTMLElement) => {
            if (reduce) {
                anim.style.height = "auto";
                anim.style.overflow = "visible";
                return;
            }

            // measure target
            const target = anim.scrollHeight;

            // ensure transition
            setTransition(anim);

            // if height is empty/auto, start from 0 so it animates
            let startFromZero = anim.style.height === "" || anim.style.height === "auto";

            // cancel in-flight transition cleanly
            cancelTransition(anim);

            if (startFromZero) {
                anim.style.height = "0px";
                anim.style.overflow = "hidden";
                forceReflow(anim); // commit 0px
            } else {
                // start from current pixel height
                anim.style.height = `${getHeight(anim)}px`;
                anim.style.overflow = "hidden";
                forceReflow(anim);
            }

            // restore transition and animate to the target height
            setTransition(anim);
            requestAnimationFrame(() => {
                anim.style.height = `${target}px`;
            });
        };

        const closeAnim = (anim: HTMLElement) => {
            if (reduce) {
                anim.style.height = "0px";
                anim.style.overflow = "hidden";
                return;
            }

            // ensure transition
            setTransition(anim);

            // cancel any in-flight transition
            cancelTransition(anim);

            // if currently auto/empty, snap to pixel height first so it can animate down
            if (anim.style.height === "" || anim.style.height === "auto") {
                anim.style.height = `${anim.scrollHeight}px`;
                anim.style.overflow = "hidden";
                forceReflow(anim);
            } else {
                // make sure it starts from the actual current height
                anim.style.height = `${getHeight(anim)}px`;
                anim.style.overflow = "hidden";
                forceReflow(anim);
            }

            // animate down to 0
            setTransition(anim);
            requestAnimationFrame(() => {
                anim.style.height = "0px";
            });
        };

        const onToggleFactory = (details: HTMLDetailsElement, anim: HTMLElement) => {
            return () => {
                setTransition(anim, "height .5s ease"); // same both ways

                if (details.open) {
                    openAnim(anim);
                } else {
                    closeAnim(anim);
                }
            };
        };

        const onTransitionEndFactory = (details: HTMLDetailsElement, anim: HTMLElement) => {
            return (e: TransitionEvent) => {
                if (e.propertyName !== "height") return;
                // When finished opening, set to auto so content inside can grow naturally
                if (details.open) {
                    anim.style.height = "auto";
                    anim.style.overflow = "visible";
                } else {
                    // When finished closing, keep it at 0 & hidden
                    anim.style.height = "0px";
                    anim.style.overflow = "hidden";
                }
            };
        };

        // Initialize & bind
        detailsList.forEach((d) => {
            const anim = d.querySelector<HTMLElement>(".psa-acc__anim");
            if (!anim) return;

            // Initial state
            if (d.open) {
                anim.style.height = reduce ? "auto" : "auto";
                anim.style.overflow = "visible";
            } else {
                anim.style.height = "0px";
                anim.style.overflow = "hidden";
            }

            const toggle = onToggleFactory(d, anim);
            const transitionEnd = onTransitionEndFactory(d, anim);

            d.addEventListener("toggle", toggle);
            anim.addEventListener("transitionend", transitionEnd);

            handlerMap.set(anim, { toggle, transitionEnd });
        });

        // Cleanup
        return () => {
            detailsList.forEach((d) => {
                const anim = d.querySelector<HTMLElement>(".psa-acc__anim");
                if (!anim) return;
                const handlers = handlerMap.get(anim);
                if (!handlers) return;
                d.removeEventListener("toggle", handlers.toggle);
                anim.removeEventListener("transitionend", handlers.transitionEnd);
            });
        };
    }, []);
};

export default useDetailsSlide;
